"use client"

import * as React from 'react'
import { flexRender, type CellContext, type ColumnDef } from '@tanstack/react-table'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { DataTable, type DataTableProps } from './DataTable'
import { RowActions } from './RowActions'
import { Button } from '../primitives/button'
import { Switch } from '../primitives/switch'
import { TagsInput, type TagsInputOption } from './inputs/TagsInput'
import { ComboboxInput, type ComboboxOption } from './inputs/ComboboxInput'

type DynamicTableDraft = Record<string, unknown>

export type DynamicTableOption = {
  value: string
  label: string
  description?: string | null
}

export type DynamicTableEditorContext<T> = {
  row: T
  fieldId: string
  value: unknown
  draft: DynamicTableDraft
  setValue: (value: unknown) => void
}

type DynamicTableEditorBase<T> = {
  fieldId?: string
  placeholder?: string
  disabled?: boolean | ((ctx: DynamicTableEditorContext<T>) => boolean)
  getValue?: (row: T) => unknown
}

type DynamicTableTextEditor<T> = DynamicTableEditorBase<T> & {
  type: 'text'
}

type DynamicTableNumberEditor<T> = DynamicTableEditorBase<T> & {
  type: 'number'
  min?: number
  step?: number | 'any'
}

type DynamicTableSelectEditor<T> = DynamicTableEditorBase<T> & {
  type: 'select'
  options:
    | DynamicTableOption[]
    | ((ctx: DynamicTableEditorContext<T>) => DynamicTableOption[])
}

type DynamicTableComboboxEditor<T> = DynamicTableEditorBase<T> & {
  type: 'combobox'
  suggestions?:
    | Array<string | ComboboxOption>
    | ((ctx: DynamicTableEditorContext<T>) => Array<string | ComboboxOption>)
  loadSuggestions?: (query?: string) => Promise<Array<string | ComboboxOption>>
  allowCustomValues?: boolean
}

type DynamicTableCheckboxEditor<T> = DynamicTableEditorBase<T> & {
  type: 'checkbox'
}

type DynamicTableTagsEditor<T> = DynamicTableEditorBase<T> & {
  type: 'tags'
  suggestions?:
    | Array<string | TagsInputOption>
    | ((ctx: DynamicTableEditorContext<T>) => Array<string | TagsInputOption>)
  loadSuggestions?: (query?: string) => Promise<Array<string | TagsInputOption>>
  allowCustomValues?: boolean
}

export type DynamicTableEditor<T> =
  | DynamicTableTextEditor<T>
  | DynamicTableNumberEditor<T>
  | DynamicTableSelectEditor<T>
  | DynamicTableComboboxEditor<T>
  | DynamicTableCheckboxEditor<T>
  | DynamicTableTagsEditor<T>

export type DynamicTableColumn<T> = ColumnDef<T, unknown> & {
  editor?: DynamicTableEditor<T>
}

export type DynamicTableRowActionHelpers<T> = {
  isEditing: boolean
  isSaving: boolean
  draft: DynamicTableDraft
  startEdit: () => void
  cancelEdit: () => void
  saveEdit: () => Promise<boolean>
}

export type DynamicTableProps<T extends object> = Omit<DataTableProps<T>, 'columns' | 'rowActions' | 'disableRowClick'> & {
  columns: DynamicTableColumn<T>[]
  getRowId?: (row: T) => string
  onSaveEdit?: (row: T, draft: DynamicTableDraft) => Promise<void> | void
  rowActions?: (row: T, helpers: DynamicTableRowActionHelpers<T>) => React.ReactNode
}

function resolveFieldId<T>(column: DynamicTableColumn<T>): string | null {
  if (column.editor?.fieldId) return column.editor.fieldId
  if ('accessorKey' in column && typeof column.accessorKey === 'string') return column.accessorKey
  if (typeof column.id === 'string' && column.id.length > 0) return column.id
  return null
}

function resolveEditorValue<T>(row: T, column: DynamicTableColumn<T>): unknown {
  if (typeof column.editor?.getValue === 'function') {
    return column.editor.getValue(row)
  }
  if ('accessorKey' in column && typeof column.accessorKey === 'string') {
    return (row as Record<string, unknown>)[column.accessorKey]
  }
  return undefined
}

function defaultCellRenderer<T>(info: CellContext<T, unknown>, originalCell: DynamicTableColumn<T>['cell']) {
  if (originalCell) {
    return flexRender(originalCell, info)
  }
  const value = info.getValue()
  if (value == null) return null
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  return String(value)
}

function normalizeDraftValue(editor: DynamicTableEditor<unknown>, value: unknown): string {
  if (editor.type === 'number') {
    if (typeof value === 'number' && Number.isFinite(value)) return String(value)
    if (typeof value === 'string') return value
    return ''
  }
  if (typeof value === 'string') return value
  if (value == null) return ''
  return String(value)
}

function DynamicTableCellEditor<T>({
  editor,
  context,
}: {
  editor: DynamicTableEditor<T>
  context: DynamicTableEditorContext<T>
}) {
  const isDisabled = typeof editor.disabled === 'function' ? editor.disabled(context) : Boolean(editor.disabled)

  if (editor.type === 'checkbox') {
    return (
      <div className="flex items-center">
        <Switch
          checked={Boolean(context.value)}
          onCheckedChange={(checked) => context.setValue(checked)}
          disabled={isDisabled}
        />
      </div>
    )
  }

  if (editor.type === 'select') {
    const options = typeof editor.options === 'function' ? editor.options(context) : editor.options
    const value = typeof context.value === 'string' ? context.value : ''
    return (
      <select
        className="h-9 w-full rounded border px-2 text-sm"
        value={value}
        onChange={(event) => context.setValue(event.target.value || null)}
        disabled={isDisabled}
      >
        <option value="">—</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    )
  }

  if (editor.type === 'combobox') {
    const suggestions = typeof editor.suggestions === 'function' ? editor.suggestions(context) : editor.suggestions
    return (
      <ComboboxInput
        value={typeof context.value === 'string' ? context.value : ''}
        onChange={(next) => context.setValue(next || null)}
        placeholder={editor.placeholder}
        suggestions={suggestions}
        loadSuggestions={editor.loadSuggestions}
        allowCustomValues={editor.allowCustomValues}
        disabled={isDisabled}
      />
    )
  }

  if (editor.type === 'tags') {
    const suggestions = typeof editor.suggestions === 'function' ? editor.suggestions(context) : editor.suggestions
    return (
      <TagsInput
        value={Array.isArray(context.value) ? context.value.filter((entry): entry is string => typeof entry === 'string') : []}
        onChange={(next) => context.setValue(next)}
        placeholder={editor.placeholder}
        suggestions={suggestions}
        loadSuggestions={editor.loadSuggestions}
        allowCustomValues={editor.allowCustomValues}
        disabled={isDisabled}
      />
    )
  }

  const inputType = editor.type === 'number' ? 'number' : 'text'
  const inputValue = normalizeDraftValue(editor as DynamicTableEditor<unknown>, context.value)
  return (
    <input
      type={inputType}
      className="h-9 w-full rounded border px-2 text-sm"
      value={inputValue}
      placeholder={editor.placeholder}
      min={editor.type === 'number' ? editor.min : undefined}
      step={editor.type === 'number' ? editor.step ?? 'any' : undefined}
      onChange={(event) => {
        if (editor.type === 'number') {
          const raw = event.target.value
          context.setValue(raw === '' ? null : raw)
          return
        }
        context.setValue(event.target.value)
      }}
      disabled={isDisabled}
    />
  )
}

export function DynamicTable<T extends object>({
  columns,
  data,
  getRowId,
  onSaveEdit,
  rowActions,
  rowClickActionIds,
  ...props
}: DynamicTableProps<T>) {
  const t = useT()
  const [editingRowId, setEditingRowId] = React.useState<string | null>(null)
  const [draft, setDraft] = React.useState<DynamicTableDraft>({})
  const [isSaving, setIsSaving] = React.useState(false)

  const resolveRowId = React.useCallback(
    (row: T): string => {
      if (typeof getRowId === 'function') return getRowId(row)
      const id = (row as Record<string, unknown>).id
      if (typeof id === 'string' && id.length > 0) return id
      throw new Error('DynamicTable rows require an id field or getRowId prop.')
    },
    [getRowId],
  )

  React.useEffect(() => {
    if (!editingRowId) return
    const stillExists = data.some((row) => resolveRowId(row) === editingRowId)
    if (!stillExists) {
      setEditingRowId(null)
      setDraft({})
      setIsSaving(false)
    }
  }, [data, editingRowId, resolveRowId])

  const startEdit = React.useCallback((row: T) => {
    const nextDraft: DynamicTableDraft = {}
    for (const column of columns) {
      if (!column.editor) continue
      const fieldId = resolveFieldId(column)
      if (!fieldId) continue
      nextDraft[fieldId] = resolveEditorValue(row, column)
    }
    setEditingRowId(resolveRowId(row))
    setDraft(nextDraft)
  }, [columns, resolveRowId])

  const cancelEdit = React.useCallback(() => {
    setEditingRowId(null)
    setDraft({})
    setIsSaving(false)
  }, [])

  const saveEdit = React.useCallback(async (row: T): Promise<boolean> => {
    if (!onSaveEdit || isSaving) return false
    setIsSaving(true)
    try {
      await onSaveEdit(row, draft)
      setEditingRowId(null)
      setDraft({})
      return true
    } catch (error) {
      console.error('[DynamicTable] save failed', error)
      return false
    } finally {
      setIsSaving(false)
    }
  }, [draft, isSaving, onSaveEdit])

  const mappedColumns = React.useMemo<ColumnDef<T, unknown>[]>(() => {
    return columns.map((column) => {
      const originalCell = column.cell
      if (!column.editor) {
        return column
      }
      return {
        ...column,
        cell: (info: CellContext<T, unknown>) => {
          const row = info.row.original
          const rowId = resolveRowId(row)
          if (rowId !== editingRowId) {
            return defaultCellRenderer(info, originalCell)
          }
          const editor = column.editor
          if (!editor) {
            return defaultCellRenderer(info, originalCell)
          }
          const fieldId = resolveFieldId(column)
          if (!fieldId) {
            return defaultCellRenderer(info, originalCell)
          }
          const value = Object.prototype.hasOwnProperty.call(draft, fieldId)
            ? draft[fieldId]
            : resolveEditorValue(row, column)
          return (
            <DynamicTableCellEditor
              editor={editor}
              context={{
                row,
                fieldId,
                value,
                draft,
                setValue: (nextValue) => {
                  setDraft((prev) => ({ ...prev, [fieldId]: nextValue }))
                },
              }}
            />
          )
        },
      }
    })
  }, [columns, draft, editingRowId, resolveRowId])

  const resolvedRowActions = React.useCallback((row: T) => {
    const helpers: DynamicTableRowActionHelpers<T> = {
      isEditing: resolveRowId(row) === editingRowId,
      isSaving,
      draft,
      startEdit: () => startEdit(row),
      cancelEdit,
      saveEdit: () => saveEdit(row),
    }
    if (rowActions) {
      return rowActions(row, helpers)
    }
    if (!onSaveEdit) return null
    if (helpers.isEditing) {
      return (
        <RowActions
          items={[
            {
              id: 'save',
              label: isSaving ? t('ui.dynamicTable.actions.saving', 'Saving…') : t('ui.dynamicTable.actions.save', 'Save'),
              onSelect: () => {
                void helpers.saveEdit()
              },
            },
            {
              id: 'cancel',
              label: t('ui.dynamicTable.actions.cancel', 'Cancel'),
              onSelect: helpers.cancelEdit,
            },
          ]}
        />
      )
    }
    return (
      <RowActions
        items={[
          {
            id: 'edit',
            label: t('ui.dynamicTable.actions.edit', 'Edit'),
            onSelect: helpers.startEdit,
          },
        ]}
      />
    )
  }, [cancelEdit, draft, editingRowId, isSaving, onSaveEdit, resolveRowId, rowActions, saveEdit, startEdit, t])

  const resolvedToolbar = React.useMemo(() => {
    if (!editingRowId) return props.toolbar
    return (
      <div className="flex flex-wrap items-center gap-2">
        {props.toolbar}
        <div className="ml-auto flex items-center gap-2 rounded-md border border-dashed px-3 py-1 text-sm text-muted-foreground">
          <span>{t('ui.dynamicTable.state.editingInline', 'Inline editing is active')}</span>
          <Button type="button" variant="ghost" size="sm" onClick={cancelEdit}>
            {t('ui.dynamicTable.actions.cancel', 'Cancel')}
          </Button>
        </div>
      </div>
    )
  }, [cancelEdit, editingRowId, props.toolbar, t])

  return (
    <DataTable
      {...props}
      columns={mappedColumns}
      data={data}
      toolbar={resolvedToolbar}
      rowActions={resolvedRowActions}
      rowClickActionIds={rowClickActionIds ?? ['open']}
      disableRowClick={Boolean(editingRowId)}
    />
  )
}
